import React, { useEffect, useState, useCallback, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, useFieldArray, Controller, UseFormSetValue } from 'react-hook-form';
import { Box, Button, CircularProgress } from '@material-ui/core';
import { TextField, Alert } from '@mui/material';

import notes from 'assets/onboarding/notes.svg';
import { ModalTitle } from 'components/Modal/Modal';
import { ModalStep } from 'components/Flow/components/ModalStep';
import { StepComponentProps } from 'components/Flow/RenderMachine';
import { selectCategories, selectSDGs } from 'entities/cause/selectors';
import Img from 'retired/Img';
import CauseApi from 'entities/cause/api';
import { selectOrganization, selectUser } from 'entities/user/selectors';
import Organization from 'entities/organization/model';

import { NonprofitOnboardingFlowContext } from '../../nonprofitOnboarding/context';

import { CausesControlledInput } from './CausesControlledInput';
import { PresenceFormData, CauseFieldName, CauseField } from './types';
import { PrevNextButtons } from './PrevNextButtons';

interface PreselectFormOptionsProps {
  organization: Organization | undefined;
  optionsMap: Map<string, unknown>;
  control: {
    appendCategory: (value: { id: string } | { id: string }[]) => void;
    appendSDG: (value: { id: string } | { id: string }[]) => void;
  };
  setValue: UseFormSetValue<PresenceFormData>;
}

const usePreselectFormOptions = ({
  organization,
  optionsMap,
  control,
  setValue,
}: PreselectFormOptionsProps): {
  categoriesInitialized: boolean;
  SDGsInitialized: boolean;
} => {
  const [initialization, setInitialization] = useState({
    categoriesInitialized: false,
    SDGsInitialized: false,
  });

  const preselectOptions = ({
    optionType,
    append,
  }: {
    optionType: CauseFieldName;
    append: (value: { id: string } | { id: string }[]) => void;
  }) => {
    if (!initialization[`${optionType}Initialized`] && organization?.[optionType]) {
      const preselectedOptions = organization[optionType]
        .toJS()
        .map((item) => optionsMap.get(item)?.toJS())
        .filter(Boolean);

      if (organization[optionType].toJS().length === preselectedOptions.length) {
        preselectedOptions.forEach((option) => {
          append(option);
          setValue(optionType, preselectedOptions);
        });
        setInitialization((prev) => ({ ...prev, [`${optionType}Initialized`]: true }));
      }
    }
  };

  useEffect(() => {
    preselectOptions({ optionType: 'categories', append: control.appendCategory });
    preselectOptions({ optionType: 'SDGs', append: control.appendSDG });
  }, [organization, optionsMap, control, setValue, initialization]);

  return initialization;
};

export const Presence = ({ send, isTransitionAllowed, state }: StepComponentProps): JSX.Element => {
  const SDGs = useSelector(selectSDGs);
  const categories = useSelector(selectCategories);
  const organization = useSelector(selectOrganization);
  const user = useSelector(selectUser);
  const [causeFields, setCauseFields] = useState<CauseField<CauseFieldName>[]>([]);

  const isSubmitting = Boolean(
    typeof state.value === 'object' && 'presence' in state.value
      ? state.value.presence === 'submitting'
      : false
  );

  const dispatch = useDispatch();
  useEffect(() => {
    const subscription = CauseApi.fetchAll().subscribe((action) => dispatch(action));
    return () => subscription.unsubscribe();
  }, [dispatch]);

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors, isSubmitted },
    watch,
  } = useForm<PresenceFormData>({
    mode: 'onBlur',
    defaultValues: {
      brief: organization?.brief,
      categories: [],
      SDGs: [],
    },
  });

  const { append: appendCategory, replace: removeCategory } = useFieldArray({
    control,
    name: 'categories',
    rules: {
      required: true,
      minLength: {
        value: 1,
        message: 'Please select at least one category',
      },
      maxLength: {
        value: 5,
        message: 'You can select up to 5 categories',
      },
    },
  });
  const { append: appendSDG, replace: removeSDG } = useFieldArray({
    control,
    name: 'SDGs',
    rules: {
      required: true,
      minLength: {
        value: 1,
        message: 'Please select at least one SDG',
      },
      maxLength: {
        value: 5,
        message: 'You can select up to 5 SDGs',
      },
    },
  });

  const { categoriesInitialized, SDGsInitialized } = usePreselectFormOptions({
    organization,
    optionsMap: new Map([...categories, ...SDGs]),
    control: { appendCategory, appendSDG },
    setValue,
  });

  const [categoriesValue, sdgsValue] = watch(['categories', 'SDGs']);

  useEffect(() => {
    // Update causeFields whenever categoriesInitialized or SDGsInitialized change
    setCauseFields([
      {
        initialized: categoriesInitialized,
        name: 'categories',
        title: 'Which categories best describe your organization? (Select up to 5)',
        label: 'Categories',
        items: categories,
        append: appendCategory,
        replace: removeCategory,
        values: categoriesValue,
        maxItems: 5,
        required: true,
      },
      {
        initialized: SDGsInitialized,
        name: 'SDGs',
        title: 'Which SDGs best describe your organization? (Select up to 5)',
        label: 'SDGs',
        items: SDGs,
        append: appendSDG,
        replace: removeSDG,
        values: sdgsValue,
        maxItems: 5,
        required: true,
      },
    ]);
  }, [
    categoriesInitialized,
    SDGsInitialized,
    categories,
    appendCategory,
    removeCategory,
    categoriesValue,
    SDGs,
    appendSDG,
    removeSDG,
    sdgsValue,
  ]);

  const onSubmit = useCallback(
    (data: PresenceFormData) => {
      send({
        type: 'SUBMIT',
        data: {
          ...data,
          categories: data.categories.map((category) => category.id),
          SDGs: data.SDGs.map((sdg) => sdg.id),
        },
      });
    },
    [send]
  );

  const { isTransitionAllowed: isTransitionAllowedParent, send: sendParent } =
    useContext(NonprofitOnboardingFlowContext) || {};

  return (
    <ModalStep
      showProgressBar
      actions={
        <div style={{ display: 'flex' }}>
          {user && user.isAdmin && (
            <PrevNextButtons
              isNextTransitionAllowed={isTransitionAllowed}
              isPrevTransitionAllowed={isTransitionAllowedParent}
              sendNext={send}
              sendPrev={sendParent}
            />
          )}
          <Button
            type="button"
            variant="contained"
            disabled={isSubmitting}
            onClick={handleSubmit(onSubmit)}
          >
            Submit
          </Button>
        </div>
      }
      header={<Img src={notes} />}
      headerStyles={{
        paddingTop: 16,
      }}
    >
      <Box mb={4}>
        <ModalTitle>Please provide more information</ModalTitle>
        <form
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 28,
            height: '100%',
          }}
        >
          <Controller
            name="brief"
            control={control}
            defaultValue={organization?.brief}
            rules={{
              required: 'This field is required',
              maxLength: { value: 2000, message: 'Please use less than 2000 characters' },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Mission Statement"
                helperText={errors.brief?.message || 'What is the mission of your company?'}
                fullWidth
                required
                variant="standard"
                multiline
                error={Boolean(errors.brief)}
              />
            )}
          />

          {causeFields.map((cause) =>
            cause.initialized ? (
              <CausesControlledInput
                key={cause.name}
                cause={cause}
                control={control}
                errors={errors}
                isSubmitted={isSubmitted}
              />
            ) : (
              <Box
                key={cause.name}
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <CircularProgress />
              </Box>
            )
          )}
        </form>
        {state.context.error && (
          <Alert severity="error" style={{ marginTop: '20px' }}>
            Submission failed, please try again. If the error persists, please contact support.{' '}
          </Alert>
        )}
      </Box>
    </ModalStep>
  );
};
